import {
    Page,
    IndexTable,
    Card,
    ChoiceList,
    TextField,
    Select,
    Thumbnail,
    IndexFilters,
    useSetIndexFiltersMode,
    IndexFiltersMode,
    useIndexResourceState,
} from '@shopify/polaris';
import type { IndexFiltersProps, TabProps } from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import agent from '../../app/api/agent';
import { ImageIcon, DeleteIcon } from '@shopify/polaris-icons';

type SelectOption = { label: string; value: string };

const DEFAULT_YEAR_OPTIONS: SelectOption[] = [{ label: 'All Years', value: '' }];
const DEFAULT_MAKE_OPTIONS: SelectOption[] = [{ label: 'All Makes', value: '' }];
const DEFAULT_MODEL_OPTIONS: SelectOption[] = [{ label: 'All Models', value: '' }];


declare global {
    interface Window {
        shopify?: { toast?: { show?: (msg: string | { message: string; duration?: number }) => void } };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);


const ProductsPage: React.FC = () => {
    const navigate = useNavigate();
    const { productStore } = useStore();
    const { products, loadProducts, page, hasNext, totalCount, mappedCount } = productStore;

    const [queryValue, setQueryValue] = useState<string>('');
    const handleQueryChange = useCallback((value: string) => setQueryValue(value), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);

    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);
    const onHandleCancel = () => { };
    const [selectedTab, setSelectedTab] = useState(0);
    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];

    const [hasFitment, setHasFitment] = useState<string[]>([]);
    const handleHasFitmentChange = useCallback((value: string[]) => setHasFitment(value), []);
    const handleHasFitmentRemove = useCallback(() => setHasFitment([]), []);

    const [vendor, setVendor] = useState<string>('');
    const handleVendorChange = useCallback((value: string) => setVendor(value), []);
    const handleVendorRemove = useCallback(() => setVendor(''), []);

    const [taggedWith, setTaggedWith] = useState<string>('');
    const handleTaggedWithChange = useCallback((value: string) => setTaggedWith(value), []);
    const handleTaggedWithRemove = useCallback(() => setTaggedWith(''), []);

    const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipInitialQueryDebounceRef = useRef(true);
    const queryValueRef = useRef(queryValue);

    // Table overlay state
    const [isPaging, setIsPaging] = useState(false);

    const filters = [
        {
            key: 'hasFitment',
            label: 'Has fitments',
            filter: (
                <ChoiceList
                    title="Has fitments"
                    titleHidden
                    choices={[
                        { label: 'Has fitments', value: 'has' },
                        { label: 'No fitments', value: 'none' },
                    ]}
                    selected={hasFitment}
                    onChange={handleHasFitmentChange}
                />
            ),
            shortcut: true,
        },
        {
            key: 'vendor',
            label: 'Vendor',
            filter: (
                <TextField
                    label="Vendor"
                    value={vendor}
                    onChange={handleVendorChange}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
        {
            key: 'taggedWith',
            label: 'Tag',
            filter: (
                <TextField
                    label="Tag"
                    value={taggedWith}
                    onChange={handleTaggedWithChange}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
    ];

    const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
    if (hasFitment.length > 0) {
        appliedFilters.push({
            key: 'hasFitment',
            label: hasFitment[0] === 'has' ? 'Has fitments' : 'No fitments',
            onRemove: handleHasFitmentRemove,
        });
    }
    if (!isEmpty(vendor)) {
        appliedFilters.push({
            key: 'vendor',
            label: `Vendor is ${vendor}`,
            onRemove: handleVendorRemove,
        });
    }
    if (!isEmpty(taggedWith)) {
        appliedFilters.push({
            key: 'taggedWith',
            label: `Tagged with ${taggedWith}`,
            onRemove: handleTaggedWithRemove,
        });
    }

    const handleFiltersClearAll = useCallback(() => {
        setHasFitment([]);
        setVendor('');
        setTaggedWith('');
        handleQueryClear();
    }, [handleQueryClear]);

    const [selectedYear, setSelectedYear] = useState<string>('');
    const [selectedMake, setSelectedMake] = useState<string>('');
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [mappedOnly, setMappedOnly] = useState(false);
    const handleYearChange = useCallback((value: string) => {
        setSelectedYear(value);
        setSelectedMake('');
        setSelectedModel('');
        setMappedOnly(false);
    }, []);
    const handleMakeChange = useCallback((value: string) => {
        setSelectedMake(value);
        setSelectedModel('');
        setMappedOnly(false);
    }, []);
    const handleModelChange = useCallback((value: string) => {
        setSelectedModel(value);
        setMappedOnly(false);
    }, []);
    const [yearOptions, setYearOptions] = useState<SelectOption[]>(DEFAULT_YEAR_OPTIONS);
    const [makeOptions, setMakeOptions] = useState<SelectOption[]>(DEFAULT_MAKE_OPTIONS);
    const [modelOptions, setModelOptions] = useState<SelectOption[]>(DEFAULT_MODEL_OPTIONS);
    const [loadingYears, setLoadingYears] = useState(false);
    const [loadingMakes, setLoadingMakes] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);

    const hasVehicleContext = Boolean(selectedYear && selectedMake && selectedModel);

    // Normalize ids to strings for Polaris
    const resourceProducts = products.map((p) => ({ ...p, id: String(p.id) }));

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState(resourceProducts);

    const onSelectionChange: NonNullable<
        React.ComponentProps<typeof IndexTable>['onSelectionChange']
    > = useCallback(
        (selectionType, toggleType, selection, position) => {
            handleSelectionChange(selectionType, toggleType, selection, position);

            const pageCount = resourceProducts.length;
            const selectedCount = selectedResources.length;

            const shouldClear =
                (selectionType === 'all' &&
                    toggleType === false &&
                    (allResourcesSelected || selectedCount >= totalCount)) ||
                (selectionType !== 'all' && selectedCount >= pageCount);

            if (shouldClear) clearSelection();
        },
        [
            handleSelectionChange,
            clearSelection,
            selectedResources.length,
            resourceProducts.length,
            allResourcesSelected,
            totalCount,
        ]
    );

    const isCurrentPageFullySelected =
        !allResourcesSelected &&
        resourceProducts.length > 0 &&
        selectedResources.length === resourceProducts.length;

    const paginatedSelectAllText = allResourcesSelected
        ? `All ${totalCount} products in your store are selected.`
        : isCurrentPageFullySelected
            ? `All ${resourceProducts.length} on this page are selected.`
            : undefined;

    const paginatedSelectAllActionText =
        !allResourcesSelected && isCurrentPageFullySelected ? `Select all ${totalCount} products` : undefined;

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Product title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Product title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Fitments', value: 'fitments asc', directionLabel: 'Lowest to highest' },
        { label: 'Fitments', value: 'fitments desc', directionLabel: 'Highest to lowest' },
        { label: 'Created', value: 'created asc', directionLabel: 'Oldest first' },
        { label: 'Created', value: 'created desc', directionLabel: 'Newest first' },
        { label: 'Updated', value: 'updated asc', directionLabel: 'Oldest first' },
        { label: 'Updated', value: 'updated desc', directionLabel: 'Newest first' },
    ];
    const [sortSelected, setSortSelected] = useState(['title asc']);

    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;

    const onBulkFitmentAction = useCallback(async (action: 'add' | 'replace' | 'delete') => {
        if (selectedCount < 1) return;

        const requiresVehicleSelection = action === 'add' || action === 'replace';
        if (requiresVehicleSelection && (!selectedYear || !selectedMake || !selectedModel)) {
            showToast('Select year, make, and model before running this action');
            return;
        }

        try {
            await agent.Products.bulkFitments({
                action,
                productIds: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                scope: allResourcesSelected
                    ? {
                        query: queryValue,
                        hasFitment: hasFitment[0] || '',
                        vendor,
                        tag: taggedWith,
                    }
                    : undefined,
                year: selectedYear,
                make: selectedMake,
                model: selectedModel,
            });

            showToast(
                action === 'add'
                    ? 'Fitments added'
                    : action === 'replace'
                        ? 'Fitments replaced'
                        : 'Fitments removed'
            );

            clearSelection();
            await loadProducts(page, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedMake, selectedModel, mappedOnly);
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to update fitments');
        }
    }, [
        selectedCount,
        selectedYear,
        selectedMake,
        selectedModel,
        allResourcesSelected,
        selectedResources,
        queryValue,
        hasFitment,
        vendor,
        taggedWith,
        clearSelection,
        loadProducts,
        page,
        sortSelected,
        mappedOnly,
    ]);

    const promotedBulkActions = [
        {
            content: 'Add fitments',
            onAction: () => onBulkFitmentAction('add'),
            disabled: selectedCount < 1,
        },
    ];
    const bulkActions = [
        {
            content: 'Replace fitments (clear and reassign)',
            onAction: () => onBulkFitmentAction('replace'),
            disabled: selectedCount < 1,
        },
        {
            icon: DeleteIcon,
            content: 'Remove fitments',
            destructive: true,
            onAction: () => onBulkFitmentAction('delete'),
            disabled: selectedCount < 1,
        },
    ];

    useEffect(() => {
        queryValueRef.current = queryValue;
    }, [queryValue]);

    useEffect(() => {
        const loadYears = async () => {
            setLoadingYears(true);
            try {
                const result = await agent.Vehicles.filterOptions();
                setYearOptions([
                    ...DEFAULT_YEAR_OPTIONS,
                    ...result.years.map((year) => ({ label: year, value: year })),
                ]);
            } catch (error) {
                console.log(error);
                setYearOptions(DEFAULT_YEAR_OPTIONS);
            } finally {
                setLoadingYears(false);
            }
        };

        loadYears();
    }, []);

    useEffect(() => {
        if (!selectedYear) {
            setMakeOptions(DEFAULT_MAKE_OPTIONS);
            return;
        }

        const loadMakes = async () => {
            setLoadingMakes(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear);
                setMakeOptions([
                    ...DEFAULT_MAKE_OPTIONS,
                    ...result.makes.map((make) => ({ label: make, value: make })),
                ]);
            } catch (error) {
                console.log(error);
                setMakeOptions(DEFAULT_MAKE_OPTIONS);
            } finally {
                setLoadingMakes(false);
            }
        };

        loadMakes();
    }, [selectedYear]);

    useEffect(() => {
        if (!selectedYear || !selectedMake) {
            setModelOptions(DEFAULT_MODEL_OPTIONS);
            return;
        }

        const loadModels = async () => {
            setLoadingModels(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear, selectedMake);
                setModelOptions([
                    ...DEFAULT_MODEL_OPTIONS,
                    ...result.models.map((model) => ({ label: model, value: model })),
                ]);
            } catch (error) {
                console.log(error);
                setModelOptions(DEFAULT_MODEL_OPTIONS);
            } finally {
                setLoadingModels(false);
            }
        };

        loadModels();
    }, [selectedYear, selectedMake]);

    // Load data immediately for non-query changes (filters/sort).
    useEffect(() => {
        if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
        productStore.loadProducts(1, queryValueRef.current, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedMake, selectedModel, mappedOnly);
    }, [productStore, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedMake, selectedModel, mappedOnly]);

    // Keep debounce only for free-text search changes.
    useEffect(() => {
        if (skipInitialQueryDebounceRef.current) {
            skipInitialQueryDebounceRef.current = false;
            return;
        }

        if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);

        queryDebounceRef.current = setTimeout(() => {
            productStore.loadProducts(1, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedMake, selectedModel, mappedOnly);
        }, 500);

        return () => {
            if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
        };
    }, [productStore, queryValue, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedMake, selectedModel, mappedOnly]);

    // One handler for next/prev that scrolls + overlays table
    const runPagedLoad = useCallback(
        async (targetPage: number) => {
            // Scroll immediately (smooth)
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Overlay only the table area while loading
            setIsPaging(true);
            try {
                await Promise.resolve(
                    loadProducts(targetPage, queryValue, hasFitment[0] || '', vendor, taggedWith, sortSelected[0], selectedYear, selectedMake, selectedModel, mappedOnly)
                );
            } finally {
                setIsPaging(false);
            }
        },
        [loadProducts, queryValue, hasFitment, vendor, taggedWith, sortSelected, selectedYear, selectedMake, selectedModel, mappedOnly]
    );

    const tableIsBusy = isPaging || productStore.loadingInitial;

    const handleTableClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const clickTarget = event.target as HTMLElement;
        const clickedCheckboxColumn = clickTarget.closest('td:first-child');

        if (!clickedCheckboxColumn) {
            return;
        }

        const checkboxElement = clickedCheckboxColumn.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (!checkboxElement || clickTarget === checkboxElement) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        checkboxElement.click();
    }, []);

    return (
        <Page fullWidth title="Products">
            <Card>
                {/* Year/Make/Model selectors */}
                <div style={{ display: 'flex', gap: 16, margin: '16px 10px' }}>
                    <div style={{ flex: 1 }}>
                        <Select label="Year" options={yearOptions} onChange={handleYearChange} value={selectedYear} disabled={loadingYears} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Select label="Make" options={makeOptions} onChange={handleMakeChange} value={selectedMake} disabled={loadingYears || loadingMakes || !selectedYear} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Select label="Model" options={modelOptions} onChange={handleModelChange} value={selectedModel} disabled={loadingYears || loadingMakes || loadingModels || !selectedYear || !selectedMake} />
                    </div>
                </div>


                {hasVehicleContext && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 10px 16px' }}>
                        <span style={{ fontSize: 13, color: '#616161' }}>
                            Viewing: {selectedYear} {selectedMake} {selectedModel}
                        </span>
                        <span style={{ fontSize: 13, color: '#616161' }}>Mapped: {mappedCount}</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input
                                type="checkbox"
                                checked={mappedOnly}
                                disabled={!hasVehicleContext}
                                onChange={(event) => setMappedOnly(event.target.checked)}
                            />
                            Show mapped only
                        </label>
                    </div>
                )}
                {!hasVehicleContext && (
                    <div style={{ margin: '0 10px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8a8a8a' }}>
                            <input type="checkbox" checked={false} disabled />
                            Show mapped only
                        </label>
                    </div>
                )}

                <IndexFilters
                    sortOptions={sortOptions}
                    sortSelected={sortSelected}
                    onSort={setSortSelected}
                    loading={productStore.loadingInitial}
                    cancelAction={{ onAction: onHandleCancel, disabled: false, loading: false }}
                    tabs={tabs}
                    selected={selectedTab}
                    onSelect={setSelectedTab}
                    queryValue={queryValue}
                    queryPlaceholder="Search products"
                    onQueryChange={handleQueryChange}
                    onQueryClear={handleQueryClear}
                    onClearAll={handleFiltersClearAll}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    mode={mode}
                    setMode={setMode}
                />

                {/* Wrap the table so we can overlay ONLY this area */}
                <div style={{ position: 'relative' }}>
                    {tableIsBusy && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.05)',
                                zIndex: 10,
                                pointerEvents: 'auto',
                            }}
                        />
                    )}

                    <div style={{ pointerEvents: tableIsBusy ? 'none' : 'auto' }} onClickCapture={handleTableClickCapture}>
                        <IndexTable
                            bulkActions={bulkActions}
                            promotedBulkActions={promotedBulkActions}
                            resourceName={{ singular: 'product', plural: 'products' }}
                            itemCount={totalCount}
                            hasMoreItems={totalCount > resourceProducts.length}
                            selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                            onSelectionChange={onSelectionChange}
                            paginatedSelectAllText={paginatedSelectAllText}
                            paginatedSelectAllActionText={paginatedSelectAllActionText}
                            headings={[
                                { title: 'Product' },
                                { title: 'SKU' },
                                { title: 'Fitments' },
                                { title: '' },
                            ]}
                            pagination={{
                                hasNext: hasNext && !tableIsBusy,
                                hasPrevious: page > 1 && !tableIsBusy,
                                onNext: () => runPagedLoad(page + 1),
                                onPrevious: () => runPagedLoad(page - 1),
                            }}
                        >
                            {resourceProducts.map((product, index) => (
                                <IndexTable.Row
                                    id={product.id}
                                    key={product.id}
                                    position={index}
                                    selected={allResourcesSelected ? true : selectedResources.includes(product.id)}
                                    onClick={() => navigate(`/products/${product.id}`)}
                                >
                                    <IndexTable.Cell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: product.isMappedToSelectedVehicle ? '3px solid #2f855a' : '3px solid transparent', paddingLeft: 8 }}>
                                            {product.featuredImage ? (
                                                <Thumbnail source={product.featuredImage} alt={product.title} size="small" />
                                            ) : (
                                                <Thumbnail source={ImageIcon} alt={product.title} size="small" />
                                            )}
                                            <span>{product.title}</span>
                                        </div>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{product.sku}</IndexTable.Cell>
                                    <IndexTable.Cell>{product.fitmentCount}</IndexTable.Cell>
                                </IndexTable.Row>
                            ))}
                        </IndexTable>
                    </div>
                </div>
            </Card>
        </Page>
    );
};

function isEmpty(value: string | string[]): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return value === '' || value == null;
}

export default observer(ProductsPage);
